import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { toISODatetime } from '@/lib/utils';
import type { InventoryLineItem, UpdateLineItemData, HoldPosition, ShowingType } from '@/types';
import { useShowingTypes } from '@/hooks/useShowingTypes';

interface LineItemRowProps {
  item: InventoryLineItem;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: (checked: boolean) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (data: UpdateLineItemData) => Promise<void>;
  onDelete: () => Promise<void>;
  onViewHistory?: () => void;
  businessCategory?: string | undefined;
  isLoading?: boolean;
  canEdit?: boolean;
  readOnlyHoldPosition?: boolean; // New prop to make hold position read-only
}

export default function LineItemRow({
  item,
  isSelected,
  isEditing,
  onSelect,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onViewHistory,
  businessCategory: _businessCategory,
  isLoading = false,
  canEdit = true,
  readOnlyHoldPosition = false, // Default to false for backward compatibility
}: LineItemRowProps) {
  const navigate = useNavigate();
  const [editData, setEditData] = useState<UpdateLineItemData>({});
  const [validationError, _setValidationError] = useState<string | null>(null);

  // Fetch showing types from API
  const { data: showingTypes = [] } = useShowingTypes();

  /**
   * The editable fields, as they currently stand on the item.
   *
   * SINGLE SOURCE for the edit session's baseline: the snapshot `editData` is seeded
   * with IS the snapshot `handleSave` diffs against. Adding a field to the seed but not
   * here is the bug this shape exists to prevent — it would compare against
   * `undefined`, always look changed, and be re-asserted on every save.
   */
  const editableFieldsFromItem = (): UpdateLineItemData => ({
    flightStartDate: item.flightStartDate ? item.flightStartDate.split('T')[0] : undefined,
    flightEndDate: item.flightEndDate ? item.flightEndDate.split('T')[0] : undefined,
    holdPosition: item.holdPosition,
    showingType: item.showingType,
  });

  // Initialize edit data when editing starts — ONCE per edit session.
  //
  // OPE-382: `item` is in the dependency list, and a successful save invalidates the
  // `lineItems` query, so a refetch used to re-run this effect and overwrite whatever
  // the user had typed while the row was still in edit mode. That matters now that a
  // save can be partially applied: if the field write succeeds and the position write
  // fails, the reseed replaced the user's chosen position with the server's old value,
  // the retry then saw it as unchanged and dropped it, and the documented "just retry"
  // path silently did nothing. The ref pins seeding to the false->true transition.
  //
  // The same snapshot is retained as the diff baseline, for the same reason: see
  // `handleSave`. Both live on this transition or neither does.
  const seededForEditSession = React.useRef(false);
  const editBaseline = React.useRef<UpdateLineItemData | null>(null);
  React.useEffect(() => {
    if (!isEditing) {
      seededForEditSession.current = false;
      editBaseline.current = null;
      return;
    }
    if (seededForEditSession.current) return;
    seededForEditSession.current = true;
    const seed = editableFieldsFromItem();
    editBaseline.current = seed;
    setEditData(seed);
    // Deps are [isEditing, item] deliberately: `editableFieldsFromItem` reads only
    // `item`, which is already listed, and it is re-created every render — depending on
    // the function itself would re-run this constantly and defeat the once-per-session
    // seeding the ref above exists to enforce.
  }, [isEditing, item]);

  // OPE-382: a CLEARED flight date is not a save, it is a mistake to surface.
  //
  // The row renders native date inputs, which the user can empty. An emptied input yields
  // `''`, which differs from the seeded value and so legitimately survives the diff
  // below. Neither thing you can then do with it is right: sending the raw `''` fails the
  // backend's `datetime()` schema as an opaque 400, and dropping the key silently makes
  // the payload a no-op — `handleSaveEdit` treats a resolved save as success and closes
  // the row (LineItemList.tsx), so the user sees their cleared date apparently accepted
  // while the server still holds the old one.
  //
  // So it is refused up front, through the same `validationError` gate that already
  // blocks the save and disables the button. Flight dates do not change in place at all
  // (the service rejects them; they move through POST /api/items/:id/recreate), so there
  // is no version of this edit to accept.
  const clearedDateError =
    isEditing && 'flightStartDate' in editData && !editData.flightStartDate
      ? 'Flight start date cannot be cleared. Use Recreate to change flight dates.'
      : isEditing && 'flightEndDate' in editData && !editData.flightEndDate
        ? 'Flight end date cannot be cleared. Use Recreate to change flight dates.'
        : null;
  const blockingError = validationError ?? clearedDateError;

  const handleSave = async () => {
    if (blockingError) {
      return;
    }

    // OPE-382: send ONLY the fields the chartist actually changed.
    //
    // `editData` is seeded from the item, so every field it carries would otherwise be
    // re-asserted on save — and for this payload a no-op re-assert is not free:
    //
    //  - `flightStartDate` / `flightEndDate`: `updateLineItem` REJECTS any supplied
    //    flight date outright — dates change through POST /api/items/:id/recreate, not
    //    in place (inventory-line-item-service.ts:151-164). Because the payload always
    //    carried both, EVERY edit-row save failed with "Flight dates cannot be edited
    //    in place", whatever the user had actually edited.
    //  - `holdPosition`: positions go to `set-position`, which enforces the depth cap,
    //    so re-asserting the position an item already holds is rejected on a unit that
    //    is already over cap — an unrelated edit failing on a queue problem it did not
    //    cause.
    //  - `showingType`: re-asserting it makes the payload non-empty even when nothing
    //    changed, which (a) sends a pointless write, (b) makes the parent handler
    //    believe ordinary fields were saved, so a failed position leg wrongly reports
    //    "your other changes were saved", and (c) on an item with NO showing type
    //    leaves a payload of only-undefined values, which `updateLineItem` rejects with
    //    "No fields to update" (:922) — killing a position-only save outright.
    //
    // Diffed generically rather than field by field. Three rounds of review each found
    // the next un-stripped field in this same object; enumerating the seed once and
    // comparing against it removes the class instead of the instance.
    //
    // Comparison happens in the seeded (YMD) form, before the ISO conversion below, so
    // the round-trip through `toISODatetime` (which appends milliseconds) is never
    // mistaken for an edit. `?? null` normalizes undefined-vs-null.
    //
    // Diffed against the SEED snapshot, not against `item` as it now stands. `item` can
    // change under an open row — a concurrent promotion moves this item primary ->
    // secondary and the refetch hands the new value down, while the effect above
    // deliberately does not reseed. Re-reading `item` here would then make an untouched
    // `holdPosition` look changed (seeded 'primary' vs live 'secondary'), and the save
    // would push the stale 'primary' back to `set-position`, undoing the newer
    // assignment the chartist never touched. The baseline answers the only question the
    // diff is asking: what did the user change since the row opened.
    //
    // No baseline means no seeded edit session, so there is nothing the user can have
    // changed — bail rather than fall back to diffing `item`, which is the behavior
    // above. Unreachable today (Save renders only inside the `isEditing` branch, and
    // effects run before any click), and deliberately fail-safe if that ever changes.
    const baseline = editBaseline.current as Record<string, unknown> | null;
    if (!baseline) return;

    const changed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(editData)) {
      if ((value ?? null) !== (baseline[key] ?? null)) changed[key] = value;
    }

    const dataToSave = changed as UpdateLineItemData;
    // Normalize date values before sending using the shared toISODatetime helper —
    // only for dates that survived the diff.
    //
    // Keyed on PRESENCE in the diff rather than truthiness, and kept defensive about an
    // empty value even though `clearedDateError` above blocks the save before it can get
    // here: a truthiness guard would silently forward a raw `''` to the backend's
    // `datetime()` schema as a 400, so if that gate is ever loosened this drops the key
    // instead of sending garbage.
    for (const key of ['flightStartDate', 'flightEndDate'] as const) {
      if (!(key in dataToSave)) continue;
      const iso = toISODatetime(dataToSave[key]);
      if (iso === undefined) delete dataToSave[key];
      else dataToSave[key] = iso;
    }

    await onSave(dataToSave);
  };

  const handleRowClick = () => {
    if (!canEdit && !isEditing) {
      navigate(`/items/${item.id}`);
    }
  };

  const getStatusBadge = () => {
    if (item.isExceptionHold && item.exceptionStatus) {
      const statusColors: Record<string, string> = {
        in_review: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-green-100 text-green-800',
        not_approved: 'bg-red-100 text-red-800',
        revision_required_by_sales: 'bg-orange-100 text-orange-800',
      };
      return (
        <Badge className={statusColors[item.exceptionStatus] || 'bg-neutral-100 text-neutral-900'}>
          {item.exceptionStatus.replace(/_/g, ' ').toUpperCase()}
        </Badge>
      );
    }
    
    if (item.chartingStatus) {
      const statusColors: Record<string, string> = {
        open: 'bg-outfront-purple-light text-outfront-purple',
        in_progress: 'bg-yellow-100 text-yellow-800',
        completed: 'bg-green-100 text-green-800',
        expired: 'bg-red-100 text-red-800',
        // OPE-386: cancelled line items get a consistent red token (matches hold-level cancelled badge)
        cancelled: 'bg-red-100 text-red-800',
        approval_required_by_exception: 'bg-purple-100 text-purple-800',
        revision_required_by_sales: 'bg-orange-100 text-orange-800',
      };
      return (
        <Badge className={statusColors[item.chartingStatus] || 'bg-neutral-100 text-neutral-900'}>
          {item.chartingStatus.replace(/_/g, ' ').toUpperCase()}
        </Badge>
      );
    }

    return <Badge className="bg-neutral-100 text-neutral-900">PENDING</Badge>;
  };

  const getHoldPositionBadge = (position?: string) => {
    if (!position) return <span className="text-xs text-neutral-400">None</span>;
    
    const positionColors: Record<string, string> = {
      primary: 'bg-outfront-purple-light text-outfront-purple',
      secondary: 'bg-purple-100 text-purple-800',
      tertiary: 'bg-orange-100 text-orange-800',
    };
    
    const positionLabels: Record<string, string> = {
      primary: 'Primary',
      secondary: 'Secondary',
      tertiary: 'Tertiary',
    };
    
    return (
      <Badge className={positionColors[position] || 'bg-neutral-100 text-neutral-900'}>
        {positionLabels[position] || position}
      </Badge>
    );
  };

  if (isEditing) {
    return (
      <tr className="bg-outfront-purple-light">
        {canEdit && (
          <td className="px-4 py-3">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
              disabled
            />
          </td>
        )}
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-neutral-900">{item.displayName}</div>
          <div className="text-xs text-neutral-400">{item.market} • {item.mediaType}</div>
        </td>
        <td className="px-4 py-3 text-sm text-right text-neutral-900">
          {item.qty ?? 1} / {item.digitalOnlyNoOfSpots != null ? item.digitalOnlyNoOfSpots : '—'}
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-neutral-400">
            {item.holdExpirationDate ? new Date(item.holdExpirationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="space-y-2">
            <input
              type="date"
              className="w-full text-sm rounded border border-neutral-200 px-2 py-1"
              value={editData.flightStartDate || ''}
              onChange={(e) => setEditData({ ...editData, flightStartDate: e.target.value })}
            />
            <input
              type="date"
              className="w-full text-sm rounded border border-neutral-200 px-2 py-1"
              value={editData.flightEndDate || ''}
              onChange={(e) => setEditData({ ...editData, flightEndDate: e.target.value })}
            />
          </div>
          {blockingError && (
            <p className="text-xs text-red-600 mt-1">{blockingError}</p>
          )}
        </td>
        <td className="px-4 py-3">
          {getStatusBadge()}
        </td>
        <td className="px-4 py-3">
          <select
            className="w-full text-sm rounded border border-neutral-200 px-2 py-1"
            value={editData.holdPosition || ''}
            onChange={(e) => setEditData({ ...editData, holdPosition: (e.target.value || null) as HoldPosition | null })}
          >
            <option value="">None</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="tertiary">Tertiary</option>
          </select>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-neutral-700">
            {showingTypes.find(s => s.code === item.showingType)?.label || item.showingType || '—'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={!!blockingError}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelEdit}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr 
      className={`hover:bg-neutral-50 ${!canEdit ? 'cursor-pointer' : ''}`}
      onClick={handleRowClick}
    >
      {canEdit && (
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
          />
        </td>
      )}
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-neutral-900">{item.displayName}</div>
        <div className="text-xs text-neutral-400">{item.market} • {item.mediaType}</div>
        {item.isExceptionHold && (
          <Badge className="mt-1 bg-yellow-100 text-yellow-800 text-xs">Exception</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-right text-neutral-900">
        {item.qty ?? 1} / {item.digitalOnlyNoOfSpots != null ? item.digitalOnlyNoOfSpots : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-neutral-900">
          {item.holdExpirationDate ? new Date(item.holdExpirationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-neutral-900">
          {new Date(item.flightStartDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
        </div>
        <div className="text-sm text-neutral-900">
          {new Date(item.flightEndDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
        </div>
      </td>
      <td className="px-4 py-3">
        {getStatusBadge()}
      </td>
      <td className="px-4 py-3">
        {canEdit && !readOnlyHoldPosition ? (
          <select
            className="w-full text-sm rounded border border-neutral-200 px-2 py-1 bg-white"
            value={item.holdPosition || ''}
            onChange={(e) => {
              e.stopPropagation();
              const newPosition = e.target.value || null;
              onSave({ holdPosition: newPosition as HoldPosition | null });
            }}
            disabled={isLoading}
          >
            <option value="">None</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="tertiary">Tertiary</option>
          </select>
        ) : (
          getHoldPositionBadge(item.holdPosition)
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-neutral-700">
          {showingTypes.find(s => s.code === item.showingType)?.label || item.showingType || '—'}
        </span>
      </td>
      {canEdit && (
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/items/${item.id}`)}
              disabled={isLoading}
            >
              View Details
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onEdit}
              disabled={isLoading}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              disabled={isLoading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete
            </Button>
            {onViewHistory && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onViewHistory}
                disabled={isLoading}
              >
                History
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}


